import axios, { AxiosError } from "axios";

type ApiOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  withCsrf?: boolean;
};

/**
 * Origin of the API, without a trailing slash.
 *
 * Previously a hardcoded constant that was hand-edited to switch between
 * localhost and production — which is how the wrong origin ships.
 *
 * `__API_URL__` is substituted at build time by portal/build.ts (and the
 * equivalent in any other app) from BUN_PUBLIC_API_URL. It is deliberately a
 * bare identifier rather than `process.env.X`: a `process` lookup has to be
 * guarded with `typeof process !== "undefined"` for the browser, and that
 * guard then wins over the substituted value — silently shipping the localhost
 * fallback to production. Substituting the identifier itself leaves no runtime
 * condition to get wrong.
 *
 * The dev server performs no substitution, so `typeof` is "undefined" there
 * and the localhost default applies, which is what dev wants.
 */
declare const __API_URL__: string | undefined;

function resolveApiTarget() {
  const injected = typeof __API_URL__ === "string" ? __API_URL__ : undefined;
  return (injected || "http://localhost:5001").replace(/\/+$/, "");
}

export const API_TARGET = resolveApiTarget();

export const apiClient = axios.create({
  baseURL: `${API_TARGET}/api`,
  withCredentials: true,
});

export function getCsrfToken() {
  return localStorage.getItem("mhj_csrf") ?? "";
}

export function setCsrfToken(token: string) {
  localStorage.setItem("mhj_csrf", token);
}

export async function apiRequest<T>(path: string, options: ApiOptions = {}) {
  const headers: Record<string, string> = {
    ...options.headers,
  };

  if (options.withCsrf) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
  }

  try {
    const response = await apiClient.request<T>({
      url: path,
      method: options.method ?? "GET",
      data: options.data,
      params: options.params,
      headers,
    });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ error?: string }>;
    const message =
      axiosError.response?.data?.error ??
      axiosError.message ??
      "Request failed";
    throw new Error(message);
  }
}
